import React from 'react';
import { Card, CardContent, Typography, LinearProgress, Box } from '@mui/material';
import { MockTestData } from '../../models/MockTestData';
import './style.css';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { selectTest } from '../../store/slices/testSlice';
// Remove unused import:
// import { initTestProgress } from '../../store/slices/testSessionProgressSlice';

interface MockTestCardProps extends MockTestData {
  seriesIndex?: number;
  testIndex?: number;
}

const MockTestCard: React.FC<MockTestCardProps> = ({
  sessionId,  // Changed from testId
  title,
  subject,
  progress,
  color,
  description,
  totalQuestions,
  completedQuestions,
  nextTestIn,
  date,
  buttonText,
  variant,
  status,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const getSubjectColor = () => {
    switch (variant) {
      case 'maths':
        return '#2563EB'; // Blue color for Maths
      case 'science':
        return '#06B6D4'; // Cyan color for Science
      case 'english':
        return '#FF8C00'; // Orange color for English
      default:
        return color;
    }
  };

  const getTestStatusText = () => {
    switch (status) {
      case 'open':
        return nextTestIn ? `Test closes in ${nextTestIn}` : 'Test is open';
      case 'upcoming':
        return nextTestIn ? `Test opens in ${nextTestIn}` : 'Test opens soon';
      case 'closed':
        return `Test closed`;
      default:
        return nextTestIn ? `Next test in ${nextTestIn}` : 'Test available';
    }
  };

  const getProgressValue = () => {
    if (status === 'upcoming' || totalQuestions === 0) return 0;
    // Calculate progress from completed/total questions
    return Math.round((completedQuestions / totalQuestions) * 100);
  };

  const getProgressText = () => {
    if (status === 'upcoming' || totalQuestions === 0) return '0';
    // Use calculated progress instead of the progress prop
    return Math.round((completedQuestions / totalQuestions) * 100);
  };

  const getProgressBarColor = () => {
    return status === 'upcoming' ? '#E0E0E0' : getSubjectColor();
  };

  const getQuestionText = () => {
    // Always show the actual counts
    return `${completedQuestions || 0}/${totalQuestions || 0} Questions`;
  };

  // Around line 110-120, replace the handleStartTest function:
  const handleStartTest = () => {
    // Log selection for debugging
    
    // Create MockTestData object from component props
    const testData: MockTestData = {
      sessionId,  // Changed from testId
      title,
      subject,
      progress,
      color,
      description,
      totalQuestions,
      completedQuestions,
      nextTestIn,
      date,
      buttonText,
      variant,
      status
    };
    
    // Select test in Redux first, then navigate
    dispatch(selectTest(testData));
    navigate('/start-test');
  };

  return (
    <Card 
      className="mock-test-card"
      onClick={handleStartTest}
      sx={{
        '& .MuiLinearProgress-bar': {
          bgcolor: getProgressBarColor(),
        },
        '& .MuiLinearProgress-root': {
          bgcolor: '#F5F5F5',
        },
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
    >
      <CardContent className="card-content">
        <Typography 
          variant="h6" 
          className="title"
          sx={{ color: getSubjectColor(), fontWeight: 600 }}
        >
          {title}
        </Typography>
        
        <Box className="progress-container">
          <Box className="progress-header">
            <Typography variant="body2" className="progress-label">
              Progress
            </Typography>
            <Typography variant="body2" className="progress-value">
              {getProgressText()}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={getProgressValue()}
            className="progress-bar"
            sx={{
              '& .MuiLinearProgress-bar': {
                bgcolor: getProgressBarColor(),
              },
              '& .MuiLinearProgress-root': {
                bgcolor: '#F5F5F5',
              }
            }}
          />
        </Box>

        <Box className="footer">
          <Typography variant="body2" className="question-count">
            {getQuestionText()}
          </Typography>
          <Typography variant="body2" className="next-test">
            {getTestStatusText()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MockTestCard;